/**
 * org_setting (SCHED-AI-02) — tiny org-wide key/value settings store.
 * Readable by anyone signed in (the scheduling copilot needs the routine
 * cap); writable only by admins (RLS via is_pegasus_admin, perm_03).
 * First consumer: 'routine_cap_per_day' in Settings → AI controls.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export function useOrgSettingNumber(key: string, fallback: number) {
  return useQuery({
    queryKey: ["org_setting", key],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase
        .from("org_setting")
        .select("value")
        .eq("key", key)
        .maybeSingle();
      if (error) throw error;
      const v = Number((data as { value: unknown } | null)?.value);
      return Number.isFinite(v) && v > 0 ? v : fallback;
    },
  });
}

/** Read an org setting as JSON (string list, {start,end}, etc.), with a fallback. */
export function useOrgSettingJson<T>(key: string, fallback: T) {
  return useQuery({
    queryKey: ["org_setting", key],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<T> => {
      const { data, error } = await supabase
        .from("org_setting")
        .select("value")
        .eq("key", key)
        .maybeSingle();
      if (error) throw error;
      const v = (data as { value: unknown } | null)?.value;
      return v === null || v === undefined ? fallback : (v as T);
    },
  });
}

export function useSaveOrgSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("org_setting").upsert({
        key,
        value,
        updated_at: new Date().toISOString(),
        updated_by: userData?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["org_setting", v.key] }),
  });
}
